import React from 'react'
import { NextSeo } from 'next-seo'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const ArchivedVideoPrivacyPolicyPage: NextPageWithLayout = () => {
  return (
    <>
      <NextSeo nofollow noindex />
      <VideoPrivacyPolicy_2024_07_31 />
    </>
  )
}

ArchivedVideoPrivacyPolicyPage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/video-privacy-policy/2024-07-31.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default ArchivedVideoPrivacyPolicyPage

const bulletedListLevel1 = 'list-disc list-inside'
const bulletedListLevel2 = 'ml-4 list-circle list-inside'

/**
 *
 * Hey, are you about to make changes to this file? Read this!!!
 *
 * When the this file gets an update, the following process needs to be followed:
 *   - Copy this file and make a new file.
 *   - Name the new file after the date of the udpate using the pattern YYYY-MM-DD.
 *   - Make your changes in the new file. Don't change this copy! It's an official archive!
 *   - Make sure to update the "Updated:" date in the new file.
 *   - Update the name of the exported component in the new file to use the updated date.
 *   - Import and use the new component in the "evergreen" video privacy policy file: ./index.tsx
 *
 * That should be it for the code changes. After you have deployed and verified that the new page
 * is up in production and that the main page is using the new version of the Video Privacy Policy, then
 * you'll need to make changes in the auth service.
 *   - Go to https://auth.angel.com/admin/document
 *     - You will need an admin account for Ellis Island to access this. If you do not have access,
 *       then ask for help getting access in Slack, currently the #platform-dev channel.
 *   - Find the latest "Video Privacy Policy" document for angel.com and take note of the version number.
 *     Note that there may be other "Video Privacy Policy" documents for other websites, e.g. invest.angel.com.
 *   - Create a new "Document" using the "Add New Document" button.
 *   - The "Title" will be visible to the end user, so the title should be "Video Privacy Policy".
 *   - Set the "Source" to the fully qualified url for the newly deployed terms page.
 *     For example, https://www.angel.com/legal/video-privacy-policy/2024-07-31
 *     It is important to use the full url, because it will get stored as the document which was signed.
 *     Do not use the evergreen url as the source.
 *   - Set the "Version" to one number higher than the previous document.
 *   - Check the "Published" boolean.
 *   - Click the "Create" button.
 *
 * Now that you've created a new document, you need to add it to the sign in flow.
 *   - Go to https://auth.angel.com/admin/client
 *   - Find the "Angel Web" client application and click on it.
 *   - In the "Required Documents" section, de-select the old document and select the new one.
 *   - Scroll to the bottom of the page and click "Save Changes"
 *
 * Everything should be wired up now. The UpdatedLegalDocuments modal should appear as soon as you've
 * completed the above. You can also verify that it's working by signing out and then sign in again
 * on angel.com. When you sign in, you should be required to accept the new document before signing in.
 *
 * After verifying that the document has been updated and the flow is working for web, broadly publicize
 * the change. The other apps that rely on the web document will need to update their client applications
 * as well in Ellis Island to require the new version of the document in their sign in flows.
 *
 */

export const VideoPrivacyPolicy_2024_07_31: React.FC = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <PaddedContainer>
        <div className="flex flex-col gap-4 p-10">
          <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p data-testid="company">Angel Studios</p>
            <p data-testid="terms-of-use-header">Video Privacy Policy</p>
          </h1>
          <p className="text-center">Updated: July 31, 2024</p>

          <p className="font-bold">
            Please read this Video Privacy Policy and Consent completely. Your consent applies to all use of the
            Device(s) on which you enable the Service even when other people use your device. You are responsible for
            informing such persons of this consent and for all activities that occur under your account.
          </p>
          <p>
            You authorize Angel Studios, Inc. (“<span className="font-bold">Angel</span>” or “we,” “our” or “us”), and
            our successors and assigns, to collect and use Personal Usage Data (defined below), which is collected
            through use of the Angel Service (“<span className="font-bold">Service(s)</span>”) via the Devices on which
            you have enabled it (“<span className="font-bold">your Personal Usage Data</span>”), and to disclose that
            information to the parties listed below in the “Angel’s Sharing of Your Personal Usage Data” section.
            Capitalized terms not expressly defined in this Video Privacy Policy and Consent have the meanings ascribed
            to them in the Angel{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
              Privacy Policy
            </InternalLink>{' '}
            and{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.termsOfUse}>
              Terms of Use
            </InternalLink>
            , incorporated by reference. In the event of any conflict between such Privacy Policy and Terms of Use and
            this Video Privacy Policy and Consent, this Video Privacy Policy and Consent shall govern with regard to the
            matters herein.
          </p>

          <p className="font-semibold">Personal Usage Data</p>
          <p>
            Our primary goals in collecting Personal Usage Data are to provide and improve our Platform, conduct our
            Operations, communicate with people and enable visitors to our Platform to easily navigate and enjoy them.
            “Personal Usage Data” includes all information collected by Angel, as indicated in the Angel{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
              Privacy Policy
            </InternalLink>{' '}
            and/or Angel{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.termsOfUse}>
              Terms of Use
            </InternalLink>{' '}
            related to, connected to or otherwise associated with the video and other content consumption and other
            activities (e.g., viewing habits) on the Device(s) on which you have enabled or accessed our Service. This
            information may include, without limitation, Personal Information, Device identifier and other UID data,
            Demographic Information, Usage Information, log files, Tracking Technologies data, and information provided
            by third-party content suppliers and services vendors, such as analytics, advertising and content
            recognition technology and service providers. Personal Usage Data may include information on video or other
            content views and interactions, and about the Devices on which the Service is activated or otherwise
            accessed. Personal Usage Data may also include any other personally identifying and non-personal information
            collected through the Service or via any analytics or technology services used by Angel in connection with
            the Service.
          </p>

          <p className="font-semibold">Angel’s Use of Your Personal Usage Data</p>
          <p>
            Angel uses Personal Usage Data, along with other information Angel may collect about you, in order to manage
            your account and to provide and improve the Service, such as to recommend other content you may like and to
            serve you interest-based advertising. For more information about how we use all of your information, please
            read the Angel{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
              Privacy Policy
            </InternalLink>
            .
          </p>

          <p className="font-semibold">Consent for Angel’s Sharing of Your Personal Usage Data</p>
          <p>
            You consent to our sharing of your Personal Usage Data to third parties under the following circumstances:
          </p>
          <ul className={bulletedListLevel1}>
            <li className="mt-2">
              <span className="underline">Third Parties That You Authorize</span>. We may share your Personal Usage Data
              in accordance with consent given at the time a disclosure is sought. We and third parties may offer you
              the ability to share your Personal Usage Data with others, such as by publishing it to social media. If
              you do so, you consent to that sharing.
            </li>
            <li className="mt-2">
              <span className="underline">Our Service Providers and Content Suppliers</span>. We may use third parties
              engaged as contractors, licensors, or services providers to help us operate and improve the Service, such
              as for purposes of content recognition; order fulfillment; request processing; recommendation analysis;
              payment and transaction processing services; information technology and telecommunication services;
              hosting services; accounting, legal, collections, and consulting services; providing content, delivering
              media and distribution services; or account management, or other business services. In the course of
              providing such services, these third parties may have access to, or be given, some or all of your Personal
              Usage Data. Including, but not limited to, service providers such Braze, Segment and Bugsnag.
            </li>

            <ul className={bulletedListLevel2}>
              <li className="mt-2">
                As the data controller, Angel Studios implements appropriate technical and organizational measures to
                protect personal data and respond to data subject access requests. Data controllers may establish
                contracts with entities (data processors) that process personal data for their established purposes (in
                this case, essential processing, analytics, and marketing).
              </li>
              <li className="mt-2">
                Data processors are multi-tenant platforms, contractually obligated to implement security and privacy
                safeguards and respond to data subject access requests per the agreements made with the data controller.
                These data processors may also use sub-processors to perform their functions in compliance with their
                security and compliance agreements.
              </li>
            </ul>

            <li className="mt-2">
              <span className="underline">In Connection with a Transaction or Business Reorganization</span>. If Angel
              transfers ownership of all or part of its business, or sells or transfers assets relating to the Angel
              Service, or is otherwise involved in a merger or business transfer, or in a bankruptcy or similar
              reorganization proceeding, Angel may share your Personal Usage Data as part of that transaction or
              proceeding (including during diligence and negotiation).
            </li>
            <li className="mt-2">
              <span className="underline">Advertisers and Ad Networks</span>. To help you get more relevant
              interest-based ads, we may share your Personal Usage Data with third party advertisers and third parties
              that facilitate the delivery of ads. For more information on advertising and what choices you may have
              regarding it, see our Privacy Policy.
            </li>
            <li className="mt-2">
              <span className="underline">Other Third Parties</span>. To the fullest extent permitted by applicable law,
              Angel may disclose your Personal Usage Data (subject to any applicable legal requirements): to law
              enforcement pursuant to a warrant, subpoena, or a court order; in a civil proceeding, pursuant to a
              subpoena or court order; to comply with a request for cooperation by a government entity; to protect
              and/or enforce our rights or the rights or safety of third parties; to deter and prevent fraud or crime;
              for Angel’s risk management purposes; to comply with our obligations and commitments regarding the privacy
              and security of your information; to prevent unauthorized, improper or illegal activities on the Service;
              to enforce our terms and rules; as otherwise permitted under our{' '}
              <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
                Privacy Policy
              </InternalLink>{' '}
              and{' '}
              <InternalLink data-testid="home-link" className="underline" href={paths.legal.termsOfUse}>
                Terms of Use
              </InternalLink>
              ; and/or as specifically disclosed when we collected the information.
            </li>
          </ul>
          <p>
            For additional information on how and to whom we share information, see our{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
              Privacy Policy
            </InternalLink>{' '}
            and{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.termsOfUse}>
              Terms of Use
            </InternalLink>
            .
          </p>

          <p className="font-semibold">Consent to Sharing of Your Protected Video Info</p>
          <p>
            Some of your Personal Usage Data may be subject to the federal Video Privacy Protection Act (“VPPA”) and
            similar state laws (“Protected Video Info”). You acknowledge that your consent herein includes giving Angel
            permission to collect, retain, use, and disclose your Protected Video Info as provided in this Video Privacy
            Policy and Consent and the Angel{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.legal.privacyPolicy}>
              Privacy Policy
            </InternalLink>
            . For Protected Video Info governed as “personally identifiable information” under the VPPA, your consent
            herein to our sharing of that “personally identifiable information” (defined as “information which
            identifies a person as having requested or obtained specific video materials or services….”) expires two (2)
            years after it is given; however, not all types of sharing require your consent. To the extent required by
            applicable law, you may at any time withdraw your consent regarding Protected Video Info by contacting us at{' '}
            <a className="underline" href="mailto:privacy@angel.com">
              privacy@angel.com
            </a>{' '}
            and stating that you desire to terminate your Protected Video Info consent. This will not affect sharing
            where consent is not required. The expiration or termination of your Protected Video Info consent may result
            in the termination of your ability to continue to use the Service, and only limits our ability to continue
            to use and share your Personal Usage Data to the extent required by applicable law. Any renewed use of the
            Services after termination or expiration is a new consent. If we obtain new consent from you after the
            expiration of the two (2) year period, this period may be extended for additional two (2) year periods each
            time we obtain new consent from you.
          </p>
          <p>
            By agreeing to this Content Privacy Policy and Consent, you are consenting to our sharing of your Protected
            Video Info, including what videos were watched and what video programming was otherwise interacted with, on
            the Device(s) on which the Service has been activated or accessed. Sharing this information will help Angel
            to provide superior service and allows you to take full advantage of the features of the Angel service.{' '}
          </p>
          <p className="font-semibold">Consent for Residents of Minnesota, New York, or Tennessee</p>
          <p>
            If you are a resident of Minnesota, New York, or Tennessee, the following is in addition to your general
            consent above. By agreeing to this Video Privacy Policy and Consent, you are effectively “signing” the
            consent in the corresponding state notices below:
          </p>
          <p>
            <span className="font-semibold">Minnesota:</span> This videotape service provider (videotape seller) from
            time to time provides to marketers of goods and services, the names and addresses of customers and a
            description or subject matter of materials rented or purchased by video customers. The videotape service
            provider (videotape seller) may not include your name, address, or the description or subject matter of any
            material rented or purchased in these lists without your written consent. This election may be changed by
            you, in writing, at any time. I do not object to the release of my name, address, or the description or
            subject matter of the material rented or purchased.
          </p>
          <p className="font-semibold">I consent and agree</p>
          <p>
            <span className="font-semibold">New York:</span> This video tape service provider from time to time provides
            to marketers of goods and services, the names and addresses of customers and a description or subject matter
            of materials rented by video customers. You have the right to elect not to have your name, address or the
            description or subject matter of any material rented included in such lists. This election may be changed by
            you, in writing, at any time. I do not object to the release of my name, address or the description or
            subject matter of the material rented.
          </p>
          <p className="font-semibold">
            I do not object to the release of my name, address or the description or subject matter of the material
            rented.
          </p>
          <p>
            <span className="font-semibold">Tennessee:</span> This video tape service provider from time to time
            provides to marketers of goods and services, the names and addresses of customers and a description or
            subject matter of materials rented by video customers. You have the right to elect not to have your name,
            address or the description or subject matter of any material rented included in such description or subject
            matter of any material rented included in such lists. This election may be changed by you, in writing, at
            any time.
          </p>
          <p className="font-semibold">I consent and agree</p>

          <p className="font-semibold">CHANGES AND UPDATES TO THIS POLICY</p>
          <p>
            Because the methods used to protect your Personal Usage Data and the services we provide are continually
            evolving, this Video Privacy Policy may change at any time. Unless otherwise noted, those changes will be
            effective as soon as they are posted. If changes are made to this Video Privacy Policy, we will make it
            known on our Platform or by email and will indicate the last date it was updated above. Please check back
            periodically to see if our Video Privacy Policy has been updated. However, we will not use your previously
            collected Protected Video Info or other Personal Information, to the extent it is not collected under an
            applicable new privacy policy, in a manner materially different than represented at the time it was
            collected without your consent.
          </p>
          <p>
            Your continued use of our Platform after any changes to this Policy become effective, constitutes your
            continued acknowledgement that you understand that we may continue to use your Personal Usage Data as
            described in this Policy.
          </p>

          <p className="font-semibold">CONTACT US</p>
          <p>
            We welcome your questions, comments and concerns about privacy. Please email us at{' '}
            <a className="underline" href="mailto:privacy@angel.com">
              privacy@angel.com
            </a>{' '}
            or contact us via the Contact Us page on the Angel Studios website with any questions or feedback you have
            pertaining to our privacy practices.
          </p>
        </div>
      </PaddedContainer>
    </div>
  )
}
